import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Mail, MapPin, Menu, X } from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";

const nav = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function PublicLayout() {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  return (
    <div className="min-h-screen flex flex-col bg-[#F8FAFC]">
      <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" data-testid="nav-home-logo"><Logo /></Link>
          <nav className="hidden md:flex items-center gap-1">
            {nav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-link-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `px-4 py-2 rounded-full text-sm font-medium transition ${
                    isActive ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100 hover:text-slate-900"
                  }`
                }
                end={n.to === "/"}
              >
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="hidden md:flex items-center gap-2">
            {user ? (
              <Button
                data-testid="nav-open-portal"
                onClick={() => navigate(user.role === "admin" ? "/admin" : "/dashboard")}
                className="rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF] text-white"
              >
                Open Portal
              </Button>
            ) : (
              <>
                <Link to="/login" data-testid="nav-login-btn">
                  <Button variant="ghost" className="rounded-full text-slate-700 hover:bg-slate-100 hover:text-slate-900">
                    Sign In
                  </Button>
                </Link>
                <Link to="/register" data-testid="nav-register-btn">
                  <Button className="rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF] text-white">
                    Client Sign Up
                  </Button>
                </Link>
              </>
            )}
          </div>
          <button
            className="md:hidden p-2 text-slate-700"
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X /> : <Menu />}
          </button>
        </div>
        {open && (
          <div className="md:hidden border-t border-slate-200 bg-white px-5 py-3 space-y-1">
            {nav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                className="block px-3 py-2 rounded-lg text-slate-700 hover:bg-slate-100"
              >
                {n.label}
              </NavLink>
            ))}
            <div className="pt-2 flex gap-2">
              <Link to="/login" className="flex-1"><Button variant="outline" className="w-full">Sign In</Button></Link>
              <Link to="/register" className="flex-1"><Button className="w-full bg-[#1E3A8A] text-white">Sign Up</Button></Link>
            </div>
          </div>
        )}
      </header>

      <main className="flex-1"><Outlet /></main>

      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-12 grid md:grid-cols-4 gap-8">
          <div className="md:col-span-2">
            <Logo />
            <p className="mt-4 text-sm text-slate-600 max-w-md leading-relaxed">
              Overseas background verification specialists. Address, litigation and identity
              checks executed to global compliance standards from our India delivery centres.
            </p>
          </div>
          <div>
            <div className="font-mono-data text-[11px] uppercase tracking-widest text-slate-500 mb-3">Contact</div>
            <a href="mailto:verificationservices@theglobalchecks.com" className="flex items-start gap-2 text-sm text-slate-700 hover:text-[#1E3A8A]">
              <Mail size={16} className="mt-0.5" /> verificationservices@theglobalchecks.com
            </a>
          </div>
          <div>
            <div className="font-mono-data text-[11px] uppercase tracking-widest text-slate-500 mb-3">Offices</div>
            <div className="text-sm text-slate-700 space-y-2">
              <div className="flex items-start gap-2"><MapPin size={16} className="mt-0.5" /> Noida, India</div>
              <div className="flex items-start gap-2"><MapPin size={16} className="mt-0.5" /> Bengaluru, India</div>
            </div>
          </div>
        </div>
        <div className="border-t border-slate-200 py-4 text-center text-xs text-slate-500">
          © {new Date().getFullYear()} The Global Checks. All rights reserved.
        </div>
      </footer>
    </div>
  );
}
